import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import tw from 'twin.macro';

import { Button } from '@components/Button';
import { Spacer } from '@components/Spacer';

import { Input } from '@auth_module/components/Input';
import { useSuccessfulLogin } from '@auth_module/hooks/useSuccessfulLogin';
import { fetchUserInfo, login, resetPassword } from '@auth_module/services';
import { useAuthParams } from '@auth_module/hooks/useAuthParams';

const StyledP = tw.p`text-sm`;
const StyledForm = tw.form`flex flex-col w-full gap-y-5`;

const ResetPasswordFormHeader = ({ email }: { email: string }) => {
  const { t } = useTranslation();

  return (
    <>
      <h3>{t('auth.reset_password_form.title')}</h3>
      <Spacer direction='vertical' size='sm' />
      <StyledP>{t('auth.reset_password_form.description', { email })}</StyledP>
    </>
  );
};

type ResetPasswordFormData = {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
};

const ResetPasswordForm = () => {
  const { t } = useTranslation();

  const [{ token: tokenParam, email: emailParam }] = useAuthParams();
  const token = useMemo(() => tokenParam || '', [tokenParam]);
  const email = useMemo(() => decodeURIComponent(emailParam || ''), [emailParam]);

  const { handleSuccessLogin } = useSuccessfulLogin({ navigateTo: '/' });

  const resetPasswordSchema = useMemo(() => {
    return yup.object().shape({
      password: yup
        .string()
        .min(8, t('auth.reset_password_form.password.error_message_min') as string)
        .max(255, t('auth.reset_password_form.password.error_message_max') as string),
      password_confirmation: yup
        .string()
        .oneOf(
          [yup.ref('password')],
          t('auth.reset_password_form.confirm_password.error_message') as string
        ),
    });
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({ resolver: yupResolver(resetPasswordSchema) });

  const onSubmit: SubmitHandler<ResetPasswordFormData> = async (
    formData: ResetPasswordFormData
  ) => {
    const requestData = { ...formData, token, email };

    const { success } = await resetPassword(requestData);

    if (success) {
      login({ password: formData.password, email })
        .then(({ data }) => {
          handleSuccessLogin(data.token);
          fetchUserInfo();
        })
        .catch((err) => console.log(`${err.error_string || err.message}`));
    }
  };

  return (
    <>
      <ResetPasswordFormHeader email={email} />
      <Spacer direction='vertical' size='xl' />

      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Input
          autoFocus
          type='password'
          label={t('auth.reset_password_form.password.label')}
          placeholder={t('auth.reset_password_form.password.placeholder')}
          {...register('password')}
          helperText='Must have at least 8 characters'
          error={errors.password?.message}
        />

        <Input
          type='password'
          label={t('auth.reset_password_form.confirm_password.label')}
          placeholder={t('auth.reset_password_form.confirm_password.placeholder')}
          {...register('password_confirmation')}
          error={errors.password_confirmation?.message}
        />

        <Button.Primary
          tw='bg-accent-2 text-text-and-icon-invert'
          type='submit'
          disabled={isSubmitting}
          isLoading={isSubmitting}>
          {t('auth.reset_password_form.submit_button')}
        </Button.Primary>
      </StyledForm>
    </>
  );
};

export { ResetPasswordForm };
